import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { publicAxios, setAuthToken } from "@/lib/axios";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { getHomeRouteForObjective } from "@/hooks/use-home-route";
import { client } from "@/lib/apollo-client";
import { getUtmParams } from "@/lib/utm-params";
import { CLAIM_REFERRAL_ATTRIBUTION } from "@/queries/queries";
import { rememberLastUsedLoginMethod } from "@/lib/auth/lastUsedLoginMethod";
import { consumeOAuthCallbackParams } from "@/lib/auth/oauthCallbackParams";
import { SUPER_HERO_ENTRY_PATH } from '@/lib/campaigns/superHeroCampaign';

const LoginGoogleCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [retryPath, setRetryPath] = useState('/login');
  const callbackStarted = useRef(false);

  useEffect(() => {
    if (callbackStarted.current) return;
    callbackStarted.current = true;

    const handleCallback = async () => {
      try {
        const callbackParams = consumeOAuthCallbackParams(location.search, location.pathname);
        const accessToken = callbackParams.get("access_token");
        const errorParam = callbackParams.get("error");
        const redirectParam = callbackParams.get("redirect");
        setRetryPath(redirectParam === SUPER_HERO_ENTRY_PATH ? SUPER_HERO_ENTRY_PATH : '/login');

        if (errorParam) {
          setError(t('auth.authProviderFailed', { provider: 'Google' }));
          return;
        }

        if (!accessToken) {
          setError(t('auth.authTokenNotFound'));
          return;
        }

        const response = await publicAxios.request({
          url: "/api/auth/google/callback",
          params: {
            access_token: accessToken,
          },
          method: "GET",
        });

        const { jwt } = response.data;

        // Set the token and user data
        setAuthToken(jwt);
        rememberLastUsedLoginMethod(localStorage, 'google');

        // Refresh user data from API
        const user = await refreshUser();

        // OAuth signups carry no register payload, so referral attribution is
        // claimed here. Fire-and-forget: the backend no-ops for accounts that
        // are older than 24h or already attributed, so calling on every OAuth
        // login is safe — and it must never block the login flow.
        const storedAffiliate = getUtmParams().affiliate;
        if (storedAffiliate) {
          client
            .mutate({ mutation: CLAIM_REFERRAL_ATTRIBUTION, variables: { code: storedAffiliate } })
            .catch(() => {});
        }

        // Show success notification
        toast({
          title: t('auth.loginSuccess'),
          description: t('auth.welcomeBack'),
        });

        // Redirect to the intended path
        const redirectPath = redirectParam || getHomeRouteForObjective(user?.onboarding_objective);
        navigate(redirectPath);
      } catch (err) {
        console.error("Error handling Google OAuth callback:", err);
        setError(t('auth.authProcessingError'));
      }
    };

    handleCallback();
  }, [location.pathname, location.search, navigate, refreshUser, t, toast]);

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-800 border-gray-700">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img
              src="/images/autoclipper-logo-full.png"
              alt="Autoclipper Logo"
              className="h-12 w-auto"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-white">
            {error ? t('auth.authError') : t('auth.processingLogin')}
          </CardTitle>
          <CardDescription className="text-gray-400">
            {error || t('auth.processingAuthWait')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-center">
              <p className="text-red-400 mb-4">{error}</p>
              <button
                onClick={() => navigate(retryPath)}
                className="text-green-400 hover:text-green-300 underline"
              >
                {t('auth.backToLogin')}
              </button>
            </div>
          ) : (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-400"></div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginGoogleCallback;
